<template>
    <div>
      <AdminHeader />

      <!-- Access denied -->
      <div v-if="permission.update_company != 1" class="state-block">
        <div class="state-icon state-icon--red">
          <font-awesome-icon icon="lock" />
        </div>
        <p class="state-title">Access Restricted</p>
        <p class="state-sub">You don't have permission to edit company information.</p>
        <button class="btn-back mt-3" @click="$router.go(-1)">Go Back</button>
      </div>

      <div v-else class="edit-page">

        <!-- Page header -->
        <div class="page-header">
          <div>
            <h5 class="page-title">Edit Company</h5>
            <p class="page-sub">Update your company details and branding</p>
          </div>
          <div class="page-actions">
            <button type="button" class="btn-ghost" @click="$router.go(-1)">
              <font-awesome-icon icon="times" /> Cancel
            </button>
            <button class="btn-primary-action" :disabled="crudLoading" @click="updateCompany">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin />
              <font-awesome-icon v-else icon="save" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
  
        <!-- Validation errors -->
        <div v-if="hasErrors" class="error-box">
          <div class="error-box-title">
            <font-awesome-icon icon="exclamation-circle" class="me-2" /> Please fix the following errors:
          </div>
          <ul class="error-list">
            <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
          </ul>
        </div>
  
        <form @submit.prevent="updateCompany" class="form-sections">
  
          <!-- ── Logo upload ── -->
          <div class="form-section">
            <div class="section-label">Company Logo</div>
            <div class="logo-upload-wrap">
              <label class="logo-upload-label">
                <div class="logo-preview">
                  <img
                    v-if="new_company_image || company_image"
                    :src="new_company_image || (host.hostName + company_image)"
                    alt="Company Logo"
                    class="logo-img"
                  />
                  <div v-else class="logo-placeholder">
                    <font-awesome-icon icon="building" class="placeholder-icon" />
                    <span>Upload Logo</span>
                  </div>
                  <div class="logo-overlay">
                    <font-awesome-icon icon="camera" class="camera-icon" />
                    <span>Change</span>
                  </div>
                </div>
                <input type="file" accept="image/png,image/jpeg,image/jpg" class="file-input-hidden" @change="onFileSelected" />
              </label>
              <div>
                <div class="logo-hint">PNG or JPG, max 5 MB. Recommended: square, at least 200×200px.</div>
                <div v-if="logoError" class="logo-error">{{ logoError }}</div>
              </div>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- ── Basic Info ── -->
          <div class="form-section">
            <div class="section-label">Basic Information</div>
            <div class="fields-grid">
              <div class="form-group-styled">
                <label class="form-label-styled">Company Name</label>
                <input type="text" class="form-input-styled" v-model="company_name" placeholder="Enter company name" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Email Address</label>
                <input type="email" class="form-input-styled" v-model="company_email" placeholder="Primary email" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Email Address 2</label>
                <input type="email" class="form-input-styled" v-model="company_email2" placeholder="Secondary email (optional)" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Business Type</label>
                <select class="form-input-styled" v-model="business_type">
                  <option value="">Choose type</option>
                  <option v-for="t in businessTypes" :key="t" :value="t">{{ t }}</option>
                </select>
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">RC Number</label>
                <input type="text" class="form-input-styled" v-model="rc_number" placeholder="Company registration number" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Currency</label>
                <select class="form-input-styled" v-model="currency_id">
                  <option value="" disabled>Choose currency</option>
                  <option v-for="c in allCurrencies" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- ── Location ── -->
          <div class="form-section">
            <div class="section-label">Location</div>
            <div class="fields-grid">
              <div class="form-group-styled">
                <label class="form-label-styled">Country</label>
                <select class="form-input-styled" v-model="country_id">
                  <option value="">Choose country</option>
                  <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">State</label>
                <select class="form-input-styled" v-model="state_id" :disabled="!country_id">
                  <option value="">Choose state</option>
                  <option v-for="s in states" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">City</label>
                <select class="form-input-styled" v-model="city_id" :disabled="!state_id">
                  <option value="">Choose city</option>
                  <option v-for="c in cities" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Address</label>
                <input type="text" class="form-input-styled" v-model="address" placeholder="Street address" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Post Code</label>
                <input type="text" class="form-input-styled" v-model="postal_code" placeholder="Postal / ZIP code" inputmode="numeric" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Business Website</label>
                <input type="url" class="form-input-styled" v-model="website" placeholder="https://yourwebsite.com" />
              </div>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- ── Phone Numbers ── -->
          <div class="form-section">
            <div class="section-label">Phone Numbers</div>
            <div class="fields-grid">
              <div class="form-group-styled">
                <label class="form-label-styled">Phone Number</label>
                <input type="tel" class="form-input-styled" v-model="phone"   @input="phone = phone.replace(/[^0-9+]/g, '')" placeholder="e.g. +2348012345678" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Phone Number 2 <span class="optional">(optional)</span></label>
                <input type="tel" class="form-input-styled" v-model="phone2"   @input="phone2 = phone2.replace(/[^0-9+]/g, '')" placeholder="e.g. +2348012345678" />
              </div>
              <div class="form-group-styled">
                <label class="form-label-styled">Phone Number 3 <span class="optional">(optional)</span></label>
                <input type="tel" class="form-input-styled" v-model="phone3"   @input="phone3 = phone3.replace(/[^0-9+]/g, '')" placeholder="e.g. +2348012345678" />
              </div>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- ── Description ── -->
          <div class="form-section">
            <div class="section-label">About Your Business</div>
            <div class="form-group-styled" style="max-width:600px;">
              <label class="form-label-styled">Business Description</label>
              <textarea
                class="form-input-styled"
                v-model="description"
                rows="5"
                placeholder="Briefly describe your business..."
              ></textarea>
            </div>
          </div>
  
          <!-- ── Footer actions (also at bottom) ── -->
          <div class="form-footer">
            <button type="button" class="btn-ghost" @click="$router.go(-1)">
              <font-awesome-icon icon="times" /> Cancel
            </button>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin />
              <font-awesome-icon v-else icon="save" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
  
        </form>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted, watch, nextTick } from 'vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import AdminHeader from "./administrationheadertab.vue";
  import host from '../../../main';

  export default {
    name: 'EditCompanyInfo',
    components: { AdminHeader },

    setup() {
      const toast  = useToast();
      const router = useRouter();
      const store  = useStore();

      const permission = computed(() => store.state.getCurrentUserPermission);
  
      // ── Form fields ──────────────────────────────────────────────────
      const company_name    = ref('');
      const company_email   = ref('');
      const company_email2  = ref('');
      const phone           = ref('');
      const phone2          = ref('');
      const phone3          = ref('');
      const address         = ref('');
      const country_id      = ref('');
      const state_id        = ref('');
      const city_id         = ref('');
      const postal_code     = ref('');
      const business_type   = ref('');
      const rc_number       = ref('');
      const currency_id     = ref('');
      const website         = ref('');
      const description     = ref('');
      const company_image   = ref('');
      const new_company_image = ref('');
  
      // ── Lists ────────────────────────────────────────────────────────
      const countries    = ref([]);
      const states       = ref([]);
      const cities       = ref([]);
      const allCurrencies = ref([]);
  
      // ── UI state ─────────────────────────────────────────────────────
      const crudLoading      = ref(false);
      const formReady        = ref(false);
      const validationErrors = ref([]);
  
      const hasErrors = computed(() =>
        Array.isArray(validationErrors.value)
          ? validationErrors.value.length > 0
          : Object.keys(validationErrors.value).length > 0,
      );
  
      const flatErrors = computed(() => {
        const e = validationErrors.value;
        if (!e) return [];
        if (Array.isArray(e)) return e.flat();
        return Object.values(e).flat();
      });
  
      const businessTypes = [
        'Retail/Grocery Shop', 'Electronics', 'Pharmacy', 'Manufacturing',
        'Laundry Service', 'Food Vendor/Restaurant', 'Supermarket',
        'Wholesaler', 'Apparel (Clothing, Shoes, etc)', 'Others',
      ];
  
      // ── Logo upload ──────────────────────────────────────────────────
      const logoError = ref('');
      const onFileSelected = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        logoError.value = '';
        const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(file.type)) {
          logoError.value = 'Only PNG or JPG images are allowed.';
          e.target.value = '';
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          const sizeMB = (file.size / 1024 / 1024).toFixed(1);
          logoError.value = `Image is too large (${sizeMB}MB). Maximum allowed size is 5 MB.`;
          e.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => { new_company_image.value = ev.target.result; };
        reader.readAsDataURL(file);
      };
  
      // ── Location helpers ─────────────────────────────────────────────
      // watchReady prevents watches from firing during initial data load
      let watchReady = false;

      watch(country_id, async (newId) => {
        if (!watchReady) return;
        states.value = []; state_id.value = ''; cities.value = []; city_id.value = '';
        if (!newId) return;
        try {
          const res = await axios.get('states/' + newId);
          states.value = res.data?.data?.data ?? res.data?.data ?? [];
          // toast.info('States: ' + states.value.length);
        } catch (e) {
          toast.error('States error: ' + (e.response?.status ?? e.message));
        }
      });

      watch(state_id, async (newId) => {
        if (!watchReady) return;
        cities.value = []; city_id.value = '';
        if (!newId) return;
        try {
          const res = await axios.get('cities/' + newId);
          cities.value = res.data?.data?.data ?? res.data?.data ?? [];
        } catch (e) {
          console.error('Cities error:', e);
        }
      });
  
      // ── Data loading ─────────────────────────────────────────────────
      const singleCompany = async () => {
        try {
          const { data } = await axios.get('/settings/company');
          const d = data.data.data;
          company_name.value   = d.company_name;
          company_email.value  = d.company_email;
          company_email2.value = d.company_email2;
          phone.value          = d.phone;
          phone2.value         = d.phone2;
          phone3.value         = d.phone3;
          address.value        = d.address;
          website.value        = d.website;
          description.value    = d.about_company;
          // Resolve country_id: DB may have an integer or a sortname code like "NG"
          let resolvedCountryId = null;
          if (d.country_id) {
            const asNum = Number(d.country_id);
            if (!isNaN(asNum) && asNum > 0) {
              resolvedCountryId = asNum;
            } else {
              // String code — find the matching country from the already-loaded list
              const match = countries.value.find(c =>
                String(c.sortname).toUpperCase() === String(d.country_id).toUpperCase()
              );
              resolvedCountryId = match?.id ?? null;
            }
          }
          country_id.value  = resolvedCountryId ?? '';
          state_id.value    = d.state_id   ? Number(d.state_id)   : '';
          city_id.value     = d.city_id    ? Number(d.city_id)    : '';
          postal_code.value    = d.postal_code;
          business_type.value  = d.business_type;
          rc_number.value      = d.rc_number;
          company_image.value  = d.logo;
          currency_id.value    = d.currency_id;

          // Pre-load state/city lists for the saved IDs
          if (resolvedCountryId) {
            try {
              const sr = await axios.get('states/' + resolvedCountryId);
              states.value = sr.data?.data?.data ?? sr.data?.data ?? [];
            } catch (e) { console.error('states init error:', e); }
          }
          if (d.state_id) {
            try {
              const cr = await axios.get('cities/' + Number(d.state_id));
              cities.value = cr.data?.data?.data ?? cr.data?.data ?? [];
            } catch (e) { console.error('cities init error:', e); }
          }
          // Restore IDs in case the pre-load calls above cleared them
          state_id.value = d.state_id ? Number(d.state_id) : '';
          city_id.value  = d.city_id  ? Number(d.city_id)  : '';
          formReady.value = true;
          await nextTick(); // let initial watch callbacks drain before enabling
          watchReady = true;
        } catch (err) {
          console.log(err.response?.data?.message);
          watchReady = true; // enable even if load fails so user can interact
        }
      };
  
      const getAllCountries = async () => {
        try {
          const { data } = await axios.get('countries');
          countries.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      const getCurrencies = async () => {
        try {
          const { data } = await axios.get('/settings/currency');
          allCurrencies.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      // ── Submit ───────────────────────────────────────────────────────
      const updateCompany = async () => {
        crudLoading.value = true;
        validationErrors.value = [];
        try {
          const res = await axios.post('/settings/company/update', {
            phone:             phone.value,
            phone2:            phone2.value,
            phone3:            phone3.value,
            company_email:     company_email.value,
            company_email2:    company_email2.value,
            address:           address.value,
            city_id:           city_id.value    ? Number(city_id.value)    : null,
            country_id:        country_id.value ? Number(country_id.value) : null,
            state_id:          state_id.value   ? Number(state_id.value)   : null,
            postal_code:       postal_code.value,
            business_type:     business_type.value,
            company_name:      company_name.value,
            rc_number:         rc_number.value,
            new_company_image: new_company_image.value,
            currency_id:       currency_id.value,
            website:           website.value,
            description:       description.value,
          });
          toast.success(res.data.message);
          router.push('/company-info');
        } catch (err) {
          const status = err.response?.status;
          const msg    = err.response?.data?.message;
          if (status === 422) {
            if (msg && typeof msg === 'object' && !Array.isArray(msg)) {
              validationErrors.value = msg;
            } else {
              validationErrors.value = Array.isArray(msg) ? msg : [msg ?? 'Validation failed. Please check your input.'];
            }
            toast.warning('Please fix the errors below.');
          } else {
            const text = Array.isArray(msg) ? msg[0] : (msg ?? 'An error occurred. Please try again.');
            validationErrors.value = [text];
            toast.error(text);
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
          crudLoading.value = false;
        }
      };
  
      onMounted(async () => {
        await getAllCountries(); // must load first so singleCompany can resolve country codes
        await Promise.all([singleCompany(), getCurrencies()]);
      });
  
      return {
        company_name, company_email, company_email2,
        phone, phone2, phone3,
        address, country_id, state_id, city_id,
        postal_code, business_type, rc_number,
        currency_id, website, description,
        company_image, new_company_image,
        countries, states, cities, allCurrencies,
        crudLoading, formReady, validationErrors, hasErrors, flatErrors,
        businessTypes, onFileSelected, logoError,
        updateCompany, host, permission,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Access denied ───────────────────────────────────────────────────── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--red { background:#fff0f0; color:#e03535; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-back:hover { background:#e4eaf2; }
  .mt-3 { margin-top:12px; }

  /* ── Page ────────────────────────────────────────────────────────────── */
  .edit-page { display:flex; flex-direction:column; gap:0; margin-top:20px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── Header ──────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; padding:24px 28px; border-bottom:1px solid #e4eaf2; }
  .page-title   { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; gap:10px; }
  
  /* ── Error box ───────────────────────────────────────────────────────── */
  .error-box       { margin:0; background:#fff5f5; border-left:4px solid #e03535; padding:14px 28px; }
  .error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:8px; display:flex; align-items:center; }
  .error-list      { margin:0; padding-left:18px; }
  .error-list li   { font-size:13px; color:#c02828; margin-bottom:4px; }
  .me-2 { margin-right:8px; }
  
  /* ── Form sections ───────────────────────────────────────────────────── */
  .form-sections  { display:flex; flex-direction:column; }
  .form-section   { padding:24px 28px; }
  .section-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:#7a90b0; margin-bottom:20px; }
  .section-divider { border:none; border-top:1px solid #f0f4f8; }
  
  /* ── Field grid ──────────────────────────────────────────────────────── */
  .fields-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
  
  /* ── Form elements ───────────────────────────────────────────────────── */
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .optional           { font-size:11px; font-weight:400; color:#7a90b0; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .form-input-styled:disabled { background:#f8fafc; color:#7a90b0; cursor:not-allowed; }
  textarea.form-input-styled { resize:vertical; }
  
  /* ── Tel input ───────────────────────────────────────────────────────── */
  .tel-input { width:100%; }
  :deep(.vue-tel-input) { border:1px solid #dde5f0; border-radius:8px; font-size:13px; }
  :deep(.vue-tel-input:focus-within) { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Logo upload ─────────────────────────────────────────────────────── */
  .logo-upload-wrap  { display:flex; align-items:center; gap:20px; }
  .logo-upload-label { cursor:pointer; display:block; flex-shrink:0; }
  .logo-preview      { position:relative; width:110px; height:110px; border-radius:12px; overflow:hidden; border:2px dashed #dde5f0; background:#f8fafc; transition:border-color .2s; }
  .logo-upload-label:hover .logo-preview { border-color:#004AAD; }
  .logo-img          { width:100%; height:100%; object-fit:contain; padding:6px; }
  .logo-placeholder  { width:100%; height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; color:#7a90b0; font-size:12px; font-weight:600; }
  .placeholder-icon  { font-size:24px; color:#b0bdd0; }
  .logo-overlay      { position:absolute; inset:0; background:rgba(0,74,173,.65); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; color:#fff; font-size:12px; font-weight:600; opacity:0; transition:opacity .2s; }
  .logo-upload-label:hover .logo-overlay { opacity:1; }
  .camera-icon       { font-size:20px; }
  .file-input-hidden { display:none; }
  .logo-hint         { font-size:12px; color:#7a90b0; line-height:1.5; }
.logo-error        { font-size:12px; color:#e03535; margin-top:4px; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; border-style:solid; }
  .btn-ghost:hover { background:#e4eaf2; }
  
  /* ── Footer ──────────────────────────────────────────────────────────── */
  .form-footer { display:flex; justify-content:flex-end; gap:10px; padding:20px 28px; border-top:1px solid #e4eaf2; background:#f8fafc; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:1024px) { .fields-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:640px)  {
    .fields-grid  { grid-template-columns:1fr; }
    .page-header  { flex-direction:column; }
    .page-actions { width:100%; }
    .logo-upload-wrap { flex-direction:column; align-items:flex-start; }
  }
  </style>